import { perkpointApi } from "./client";

/* ------------------------------------------------------------------ */
/* Create payload (POST /admin/programs/:programId/campaigns)          */
/* ------------------------------------------------------------------ */

export type ApiCampaignType =
  | "promotional"
  | "lifecycle"
  | "referral"
  | "seasonal"
  | "tier";

export type ApiCampaignReward =
  | { enabled: false }
  | {
      enabled: true;
      type: "multiply" | "fixed";
      multiplier?: number;
      points?: number;
      when: string;
      cap_per_member?: number;
      total_budget?: number;
    }
  | {
      enabled?: boolean;
      type?: "multiply" | "fixed";
      multiplier?: number;
      points?: number;
      when?: string;
      cap_per_member?: number;
      total_budget?: number;
    };

export type ApiCampaignAudience = {
  all_members: boolean;
  segment_ids: string[];
  include_tier_ids: string[];
  exclude_tier_ids: string[];
  control_group_pct: number;
};

export type ApiCampaignChannel =
  | {
      channel: "whatsapp";
      template_name: string;
      header?: string;
      body: string;
      footer?: string;
      button_label?: string;
      button_link?: string;
    }
  | { channel: "email"; subject: string; body: string; html_body?: string }
  | { channel: "sms"; body: string }
  | { channel: "in_app"; title: string; body: string }
  | { channel: "web"; title: string; body: string; image_url?: string };

type ApiScheduleCommon = {
  time_zone: string;
  ends_at?: string;
  respect_quiet_hours: boolean;
  max_messages_per_day?: number;
};

export type ApiCampaignSchedule =
  | (ApiScheduleCommon & { mode: "once"; send_at: string })
  | (ApiScheduleCommon & {
      mode: "repeating";
      repeat: {
        frequency: "daily" | "weekly" | "monthly";
        time: string;
        day_of_week?: number;
        day_of_month?: number;
      };
    })
  | (ApiScheduleCommon & {
      mode: "event";
      event_type_key: string;
      delay_minutes?: number;
    });

export type CreateCampaignPayload = {
  name: string;
  description?: string;
  campaign_type?: ApiCampaignType;
  reward?: ApiCampaignReward;
  audience?: ApiCampaignAudience;
  channels?: ApiCampaignChannel[];
  schedule?: ApiCampaignSchedule;
};

/** PATCH body — same nested shape as create; send only changed sections. */
export type UpdateCampaignPayload = Partial<CreateCampaignPayload>;

/** Statuses the API allows to edit. Others return 409 campaign_locked. */
export const EDITABLE_CAMPAIGN_STATUSES = [
  "draft",
  "pending_approval",
  "scheduled",
  "paused",
] as const;

export function isCampaignEditable(status: string | null | undefined): boolean {
  return EDITABLE_CAMPAIGN_STATUSES.includes(
    (status || "").toLowerCase() as (typeof EDITABLE_CAMPAIGN_STATUSES)[number],
  );
}

/** List/detail shape from GET campaigns APIs */
export type ApiCampaign = {
  id: string;
  program_id?: string;
  display_code?: string | null;
  name: string;
  description?: string | null;
  status?: string;
  campaign_type?: ApiCampaignType | string | null;
  objective?: string | null;
  trigger_type?: string | null;
  scheduled_at?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  time_zone?: string | null;
  schedule?: ApiCampaignSchedule | Record<string, unknown> | null;
  reward?: ApiCampaignReward | null;
  audience?: ApiCampaignAudience & {
    segments?: { id?: string; key?: string; name?: string; label?: string }[];
    include_tiers?: { id?: string; key?: string; name?: string }[];
    exclude_tiers?: { id?: string; key?: string; name?: string }[];
  } | null;
  channels?: Array<
    | string
    | (Partial<ApiCampaignChannel> & {
        channel: string;
        order?: number;
        variables?: string[];
        subject?: string | null;
        title?: string | null;
        image_url?: string | null;
        html_body?: string | null;
      })
  > | null;
  quiet_hours_override?: {
    enabled?: boolean;
    start?: string;
    end?: string;
  } | null;
  freq_cap_override?: { days?: number; messages?: number } | null;
  control_group_pct?: string | number | null;
  budget_points?: number | null;
  goal_event_type_key?: string | null;
  attribution_window_days?: number | null;
  requires_approval?: boolean;
  trigger_event_type_key?: string | null;
  delay_seconds?: number | null;
  recurrence_cron?: string | null;
  submitted_at?: string | null;
  approved_at?: string | null;
  rejected_at?: string | null;
  rejection_reason?: string | null;
  next_run_at?: string | null;
  completed_at?: string | null;
  created_at?: string;
  updated_at?: string;
  launch_issues?: unknown[];
  [key: string]: unknown;
};

export type CampaignListQuery = {
  search?: string;
  status?: string;
  page?: number;
  pageSize?: number;
};

/** GET /admin/programs/:programId/campaigns/summary */
export type CampaignSummary = {
  total_campaigns: number;
  total_campaigns_change_pct: number;
  active_campaigns: number;
  active_campaigns_change_pct: number;
  members_reached: number;
  members_reached_change_pct: number;
  points_issued: number;
  points_issued_change_pct: number;
  redemptions: number;
  redemptions_change_pct: number;
};

/* ------------------------------------------------------------------ */
/* Calls                                                               */
/* ------------------------------------------------------------------ */

export const campaignKeys = {
  all: ["perkpoint", "campaigns"] as const,
  list: (programId: string, query: CampaignListQuery = {}) =>
    ["perkpoint", "campaigns", programId, "list", query] as const,
  detail: (programId: string, campaignId: string) =>
    ["perkpoint", "campaigns", programId, "detail", campaignId] as const,
  summary: (programId: string) =>
    ["perkpoint", "campaigns", programId, "summary"] as const,
};

const base = (programId: string) => `/admin/programs/${programId}/campaigns`;

function unwrapCampaignList(data: unknown): ApiCampaign[] {
  if (Array.isArray(data)) return data as ApiCampaign[];
  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>;
    for (const key of ["items", "data", "campaigns"]) {
      if (Array.isArray(record[key])) return record[key] as ApiCampaign[];
    }
  }
  return [];
}

export const CampaignsApi = {
  async create(programId: string, payload: CreateCampaignPayload) {
    const { data } = await perkpointApi.post<ApiCampaign>(base(programId), payload);
    return data;
  },

  async list(programId: string, query: CampaignListQuery = {}) {
    const { data } = await perkpointApi.get<unknown>(base(programId), {
      params: query,
    });
    return {
      raw: data,
      items: unwrapCampaignList(data),
    };
  },

  /** GET /admin/programs/:programId/campaigns/summary */
  async summary(programId: string) {
    const { data } = await perkpointApi.get<CampaignSummary>(
      `${base(programId)}/summary`,
    );
    return data;
  },

  async get(programId: string, campaignId: string) {
    const { data } = await perkpointApi.get<ApiCampaign>(
      `${base(programId)}/${campaignId}`,
    );
    return data;
  },

  async update(
    programId: string,
    campaignId: string,
    payload: UpdateCampaignPayload,
  ) {
    const { data } = await perkpointApi.patch<ApiCampaign>(
      `${base(programId)}/${campaignId}`,
      payload,
    );
    return data;
  },

  /** POST /admin/programs/:programId/campaigns/:campaignId/submit */
  async submit(programId: string, campaignId: string) {
    const { data } = await perkpointApi.post<ApiCampaign>(
      `${base(programId)}/${campaignId}/submit`,
    );
    return data;
  },

  /** POST /admin/programs/:programId/campaigns/:campaignId/approve */
  async approve(programId: string, campaignId: string) {
    const { data } = await perkpointApi.post<ApiCampaign>(
      `${base(programId)}/${campaignId}/approve`,
    );
    return data;
  },

  /** POST /admin/programs/:programId/campaigns/:campaignId/reject */
  async reject(programId: string, campaignId: string, reason?: string) {
    const { data } = await perkpointApi.post<ApiCampaign>(
      `${base(programId)}/${campaignId}/reject`,
      reason?.trim() ? { reason: reason.trim() } : {},
    );
    return data;
  },
};
